/**
 * MSR slice 4 — start and close each event day (migrations 2026-10-11-500 / 501).
 *
 *  - MsrEventDayRunPanel: the MSR's Overview cards on /msr/events/:id (mobile-first): a per-day Start card with the
 *    three-item checklist and a Close card with the day's numbers, the verified sales list, the "No sales today"
 *    confirmation and the close rate where the event type has one.
 *  - EventDaysStatusPanel: the admin Event Detail view of every day's status (not started / started / closed /
 *    late), where a full-time employee completes a late close and an admin adds a correction.
 *
 * Both render nothing for events outside the MSR date model. Every action is re-checked by the server.
 *
 * Migration 2026-10-30-1330: each rep may start / close THEIR OWN part of a day (optional), the day is closed by its
 * lead (or the rep named to close that day), closing the day closes any part still open, and the lead can reopen the
 * day on the day. The team list shows each rep's own part times and own credited sales -- never attendance or a close
 * rate per rep (attendance does not depend on a rep).
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  closeEventDay,
  closeMyPart,
  correctEventDayClose,
  DayRunError,
  dayRunKeys,
  formatCloseRate,
  getEventDayRunState,
  isPermissionRefusal,
  previewCloseRate,
  reopenEventDay,
  startEventDay,
  startMyPart,
  STATUS_LABELS,
  type DayTeamMember,
  type EventRunDay,
  type EventRunState,
} from "../../services/msrDayRun.service";
import { useToast } from "../ui/Toast";
import UnsavedChangesGuard from "../common/UnsavedChangesGuard";
import { eventKeys } from "../../hooks/useEvents";
import {
  eventRunNoun,
  startDayItems,
  startDayQuestion,
} from "../../utils/eventTypePolicy";

// ------------------------------------------------------------------ shared bits
/** A close or a correction changes more than the day board (final audit P3-7): the event row (the attendance mirror,
 *  "ready to mark Completed"), the close-rate card and the Member Verification tab (a closed day's sign-ups are final). */
export function invalidateAfterDayRecord(qc: QueryClient, eventId: string) {
  for (const queryKey of [
    dayRunKeys.state(eventId),
    eventKeys.detail(eventId),
    ["event-close-rate-stats", eventId],
    ["event-vm-stats", eventId],
    ["event-signup-pool", eventId],
    ["event-attribution-summary", eventId],
    ["msr-event-attendees", eventId],
  ]) {
    void qc.invalidateQueries({ queryKey });
  }
}

/** The event (or its day) is gone: asking again will not bring it back (final audit P3-7). */
function isGoneRefusal(e: unknown): boolean {
  return e instanceof DayRunError && e.msrCode === "MSR_DAY_NOT_FOUND";
}

export function useEventDayRunState(eventId: string, enabled = true) {
  return useQuery({
    queryKey: dayRunKeys.state(eventId),
    queryFn: () => getEventDayRunState(eventId),
    enabled: !!eventId && enabled,
    // a refusal (42501: not on this event; MSR_DAY_NOT_FOUND: the event is gone) does not change by asking again:
    // no retries, no polling
    retry: (count, err) =>
      !isPermissionRefusal(err) && !isGoneRefusal(err) && count < 3,
    // poll only an event that is in the day model; stop on a permission refusal or a missing event
    refetchInterval: (query) =>
      isPermissionRefusal(query.state.error) ||
      isGoneRefusal(query.state.error) ||
      query.state.data?.model_active === false
        ? false
        : 60_000,
  });
}

const dayLabelOf = (serviceDate: string): string => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    }).format(new Date(`${serviceDate}T12:00:00Z`));
  } catch {
    return serviceDate;
  }
};

const timeOf = (iso: string | null | undefined, tz?: string): string => {
  if (!iso) {
    return "";
  }
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
      month: "short",
      day: "numeric",
      timeZone: tz,
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const CHIP: Record<string, string> = {
  not_started: "bg-gray-100 text-gray-700",
  started: "bg-blue-100 text-blue-800",
  closed: "bg-green-100 text-green-800",
  late: "bg-red-100 text-red-800",
  not_tracked: "bg-gray-50 text-gray-500",
};

export const DayStatusChip: React.FC<{ day: EventRunDay }> = ({ day }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${CHIP[day.status] ?? CHIP.not_started}`}
    data-testid="day-status-chip"
  >
    {STATUS_LABELS[day.status]}
    {day.status === "closed" && day.close?.is_late ? " (late)" : ""}
  </span>
);

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="rounded-md bg-gray-50 px-3 py-2">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-lg font-semibold text-gray-900">{value}</div>
  </div>
);

/** Numbers of a closed day: the original close, plus the latest admin correction when there is one. */
export const DayCloseSummary: React.FC<{
  day: EventRunDay;
  state: EventRunState;
}> = ({ day, state }) => {
  const c = day.close;
  if (!c) {
    return null;
  }
  const corr = c.latest_correction;
  const rate = formatCloseRate(corr ? corr.close_rate : c.close_rate);
  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-700">
        Closed by {c.closed_by_name ?? "someone"} ·{" "}
        {timeOf(c.closed_at, state.timezone)}
        {c.closed_as === "manager" ? " (completed by a manager)" : ""}
      </p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {c.attendance_source !== "none" && (
          <Stat
            label={c.attendance_source === "manual" ? "Attendees" : "Check-ins"}
            value={corr?.attendance_count ?? c.attendance_count ?? "—"}
          />
        )}
        <Stat label="Sign-ups" value={c.signups_count} />
        <Stat
          label="Verified sales"
          value={corr ? corr.verified_count : c.verified_count}
        />
        {state.has_close_rate && (
          <Stat label="Close rate" value={rate ?? "—"} />
        )}
      </div>
      {c.no_sales_confirmed && (
        <p className="text-xs text-gray-600">Closed with "No sales today".</p>
      )}
      {corr && (
        <p className="text-xs text-amber-800">
          Corrected by {corr.by_name ?? "an admin"} on{" "}
          {timeOf(corr.corrected_at, state.timezone)}: "{corr.reason}".
          Originally {c.attendance_count ?? "—"} / {c.verified_count} verified
          {c.corrections > 1 ? ` · ${c.corrections} corrections` : ""}.
        </p>
      )}
      {c.sales.length > 0 && (
        <ul className="divide-y divide-gray-100 rounded-md border border-gray-200 text-sm">
          {c.sales.map((s, i) => (
            <li key={i} className="flex justify-between px-3 py-2">
              <span>{s.name || "(no name)"}</span>
              <span className="text-gray-500">{s.rep_name ?? ""}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** The close form (used by the rep on the day and by a full-time employee after it). */
const DayCloseForm: React.FC<{
  eventId: string;
  day: EventRunDay;
  state: EventRunState;
  asManager: boolean;
}> = ({ eventId, day, state, asManager }) => {
  const qc = useQueryClient();
  const { showToast } = useToast();
  const [attendance, setAttendance] = useState("");
  const [noSales, setNoSales] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const live = day.live;
  const source = state.attendance_source ?? "none";
  const verified = live?.verified_count ?? 0;
  const attendanceNum =
    source === "manual"
      ? attendance.trim() === ""
        ? null
        : Number(attendance)
      : source === "checkins"
        ? (live?.checkins_count ?? 0)
        : null;
  const attendanceValid =
    source !== "manual" ||
    (attendanceNum !== null &&
      Number.isInteger(attendanceNum) &&
      attendanceNum >= 0 &&
      attendanceNum <= 100000);
  const rate = previewCloseRate(
    !!state.has_close_rate,
    verified,
    attendanceNum,
  );
  const ready = attendanceValid && (verified > 0 || noSales);

  const mutation = useMutation({
    mutationFn: () =>
      closeEventDay(day.event_day_id, {
        attendance: source === "manual" ? attendanceNum : null,
        noSalesConfirmed: verified === 0 ? noSales : false,
        expectedVerifiedCount: verified,
      }),
    onSuccess: () => {
      showToast("success", `${dayLabelOf(day.service_date)} closed`);
      invalidateAfterDayRecord(qc, eventId);
    },
    onError: (e: Error) => {
      setConfirming(false);
      showToast("error", e.message);
      void qc.invalidateQueries({ queryKey: dayRunKeys.state(eventId) });
    },
  });

  // Item 8: a typed attendance count or a ticked "No sales today" is lost on leaving -- ask first.
  const closeDirty =
    !mutation.isSuccess &&
    ((source === "manual" && attendance.trim() !== "") || noSales);

  return (
    <div className="space-y-3" data-testid="day-close-form">
      <UnsavedChangesGuard
        when={closeDirty}
        message="The close isn't finished. Leave and lose the numbers you entered?"
      />
      <div className="grid grid-cols-2 gap-2">
        {source === "checkins" && (
          <Stat
            label="Check-ins"
            value={
              <span>
                {live?.checkins_count ?? 0}
                <span className="ml-1 text-xs font-normal text-gray-500">
                  ({live?.registered_checkins ?? 0} registered +{" "}
                  {live?.walkin_checkins ?? 0} walk-in)
                </span>
              </span>
            }
          />
        )}
        <Stat label="Sign-ups" value={live?.signups_count ?? 0} />
        <Stat label="Verified sales" value={verified} />
        {state.has_close_rate && (
          <Stat label="Close rate" value={formatCloseRate(rate) ?? "—"} />
        )}
      </div>

      {source === "manual" && (
        <label className="block">
          <span className="text-sm font-medium text-gray-800">
            Total attendees
          </span>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            max={100000}
            step={1}
            value={attendance}
            onChange={(e) => setAttendance(e.target.value)}
            className="mt-1 block w-full min-h-[44px] rounded-md border border-gray-300 px-3 text-base"
            placeholder="How many people attended?"
            aria-label="Total attendees"
          />
          {attendance !== "" && !attendanceValid && (
            <span className="text-xs text-red-700">
              Enter a whole number from 0 to 100,000.
            </span>
          )}
        </label>
      )}

      <div>
        <p className="text-sm font-medium text-gray-800">
          Verified sales for {dayLabelOf(day.service_date)}
        </p>
        {verified === 0 ? (
          <p className="text-sm text-gray-600">
            No verified sales yet.{" "}
            {!asManager && (
              <Link
                to={`/msr/events/${eventId}?tab=attendees`}
                className="text-primary-600 underline"
              >
                Verify sign-ups
              </Link>
            )}
          </p>
        ) : (
          <ul className="mt-1 divide-y divide-gray-100 rounded-md border border-gray-200 text-sm">
            {(live?.verified ?? []).map((s, i) => (
              <li
                key={s.contact_id ?? i}
                className="flex justify-between px-3 py-2"
              >
                <span>{s.name || "(no name)"}</span>
                <span className="text-gray-500">{s.rep_name ?? ""}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {verified === 0 && (
        <label className="flex min-h-[44px] items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-3">
          <input
            type="checkbox"
            checked={noSales}
            onChange={(e) => setNoSales(e.target.checked)}
            className="h-5 w-5"
          />
          <span className="text-sm text-amber-900">No sales today</span>
        </label>
      )}

      {!confirming ? (
        <button
          type="button"
          disabled={!ready}
          onClick={() => setConfirming(true)}
          className="w-full min-h-[44px] rounded-md bg-primary-600 px-4 text-sm font-semibold text-white disabled:opacity-50"
        >
          {asManager ? "Complete the close" : "Close sales event"}
        </button>
      ) : (
        <div className="space-y-2 rounded-md border border-gray-300 p-3">
          <p className="text-sm text-gray-800">
            {asManager
              ? "Closing is final: the day can't be reopened. Only an admin can correct the numbers afterwards."
              : "Closing the sales event closes the day for the whole team, and any rep part still open is closed for them. If a rep hasn't finished, you can reopen the day until 10 minutes before the day ends."}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="flex-1 min-h-[44px] rounded-md border border-gray-300 bg-white text-sm"
            >
              Back
            </button>
            <button
              type="button"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate()}
              className="flex-1 min-h-[44px] rounded-md bg-primary-600 text-sm font-semibold text-white disabled:opacity-50"
            >
              {mutation.isPending ? "Closing…" : "Confirm close"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * RUN-02: one question, "Check into your <class / gun show / range night>. Are these items complete and ready?",
 * with the items listed under it. Answering "Yes, start the day" starts the day. The server still takes the three
 * checklist answers (msr_start_event_day refuses unless all three are true); the one "yes" confirms every listed item,
 * so it sends all three as true.
 */
const DayStartForm: React.FC<{
  eventId: string;
  day: EventRunDay;
  eventType?: string | null;
}> = ({ eventId, day, eventType }) => {
  const qc = useQueryClient();
  const { showToast } = useToast();
  const noun = eventRunNoun(eventType);
  const mutation = useMutation({
    mutationFn: () =>
      startEventDay(day.event_day_id, {
        atVenue: true,
        boothSetUp: true,
        qrSignWorking: true,
      }),
    onSuccess: (r) => {
      showToast(
        "success",
        r.already_started
          ? "Another rep already started this day"
          : "Day started",
      );
      void qc.invalidateQueries({ queryKey: dayRunKeys.state(eventId) });
    },
    onError: (e: Error) => {
      showToast("error", e.message);
      void qc.invalidateQueries({ queryKey: dayRunKeys.state(eventId) });
    },
  });
  return (
    <div className="space-y-3" data-testid="day-start-form">
      <p className="text-sm font-medium text-gray-900">
        {startDayQuestion(noun)}
      </p>
      <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700">
        {startDayItems(noun, eventType).map((it) => (
          <li key={it}>{it}</li>
        ))}
      </ul>
      <button
        type="button"
        disabled={mutation.isPending}
        onClick={() => mutation.mutate()}
        className="w-full min-h-[44px] rounded-md bg-primary-600 px-4 text-sm font-semibold text-white disabled:opacity-50"
      >
        {mutation.isPending ? "Starting…" : "Yes, start the day"}
      </button>
      <p className="text-xs text-gray-500">
        Not ready yet? Finish setting up first, then come back and start the
        day.
      </p>
    </div>
  );
};

// ------------------------------------------------------------------ rep parts (mig 1330)
const PART_BLOCK_TEXT: Record<string, string> = {
  not_started: "Your part can start once the day is started.",
  not_tagged: "You're not on the Sales Reps list for this day.",
  upcoming: "You can start your part on the day itself.",
  ended: "This day has ended.",
  closed: "This day is closed.",
  not_tracked: "This day isn't tracked for start and close.",
  other_part_open:
    "Your part at another event today is still open. Close it there first.",
};

const PARTS_OPTIONAL_TEXT =
  "Optional: your part records when you worked this day. You can skip it. Closing the day closes any part still open.";

const minutesText = (m: number): string =>
  m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`;

/** The viewer's own part: Start my part / Close my part (optional). */
export const MyPartCard: React.FC<{
  eventId: string;
  day: EventRunDay;
  timezone?: string;
}> = ({ eventId, day, timezone }) => {
  const qc = useQueryClient();
  const { showToast } = useToast();
  const [note, setNote] = useState("");
  const refresh = () =>
    void qc.invalidateQueries({ queryKey: dayRunKeys.state(eventId) });
  const start = useMutation({
    mutationFn: () => startMyPart(day.event_day_id),
    onSuccess: (r) => {
      showToast(
        "success",
        r.already_open ? "Your part is already open" : "Your part started",
      );
      refresh();
    },
    onError: (e: Error) => {
      showToast("error", e.message);
      refresh();
    },
  });
  const close = useMutation({
    mutationFn: () => closeMyPart(day.event_day_id, note),
    onSuccess: () => {
      showToast("success", "Your part is closed");
      setNote("");
      refresh();
    },
    onError: (e: Error) => {
      showToast("error", e.message);
      refresh();
    },
  });
  if (!day.i_am_tagged || day.can_start_part === undefined) {
    return null;
  }
  return (
    <div
      className="space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3"
      data-testid="my-part-card"
    >
      <p className="text-sm font-medium text-gray-900">Your part</p>
      <p className="text-xs text-gray-600">{PARTS_OPTIONAL_TEXT}</p>
      {day.my_part ? (
        <>
          <p className="text-sm text-gray-700">
            Your part started {timeOf(day.my_part.started_at, timezone)}.
          </p>
          <input
            type="text"
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional)"
            aria-label="Note for your part"
            className="block w-full min-h-[40px] rounded-md border border-gray-300 px-2 text-sm"
          />
          <button
            type="button"
            disabled={close.isPending || !day.can_close_part}
            onClick={() => close.mutate()}
            className="w-full min-h-[44px] rounded-md border border-primary-600 bg-white px-4 text-sm font-semibold text-primary-700 disabled:opacity-50"
          >
            {close.isPending ? "Closing…" : "Close my part"}
          </button>
        </>
      ) : day.can_start_part ? (
        <button
          type="button"
          disabled={start.isPending}
          onClick={() => start.mutate()}
          className="w-full min-h-[44px] rounded-md border border-primary-600 bg-white px-4 text-sm font-semibold text-primary-700 disabled:opacity-50"
        >
          {start.isPending ? "Starting…" : "Start my part"}
        </button>
      ) : (
        <p className="text-sm text-gray-600">
          {PART_BLOCK_TEXT[day.part_block ?? ""] ??
            "You can't start a part now."}
        </p>
      )}
    </div>
  );
};

const memberStatus = (m: DayTeamMember): string =>
  m.part_open ? "Working" : m.parts.length > 0 ? "Done" : "No part";

/** Who is working the day: each rep's part times and their OWN credited sales (no per-rep attendance / close rate). */
export const DayTeamList: React.FC<{ day: EventRunDay; timezone?: string }> = ({
  day,
  timezone,
}) => {
  const team = day.team ?? [];
  if (team.length === 0) {
    return null;
  }
  return (
    <div className="space-y-1" data-testid="day-team-list">
      <p className="text-sm font-medium text-gray-800">Team</p>
      <ul className="divide-y divide-gray-100 rounded-md border border-gray-200 text-sm">
        {team.map((m) => (
          <li
            key={m.profile_id}
            className="space-y-0.5 px-3 py-2"
            data-testid="day-team-member"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium text-gray-900">
                {m.name ?? "(no name)"}
                {m.is_me ? " (you)" : ""}
                {m.role === "lead" ? " · lead" : ""}
                {m.closer_role === "primary" ? " · closes today" : ""}
                {m.closer_role === "backup" ? " · backup closer" : ""}
                {!m.on_list ? " · no longer on the list" : ""}
              </span>
              <span className="text-xs text-gray-600">
                {memberStatus(m)}
                {m.credited_sales !== null
                  ? ` · ${m.credited_sales} sale${m.credited_sales === 1 ? "" : "s"}`
                  : ""}
              </span>
            </div>
            {m.parts.length > 0 && (
              <p className="text-xs text-gray-500">
                {m.parts
                  .map(
                    (p) =>
                      `${timeOf(p.started_at, timezone)} – ${
                        p.closed_at ? timeOf(p.closed_at, timezone) : "now"
                      }${p.auto_closed ? " (closed with the day)" : ""}`,
                  )
                  .join(", ")}{" "}
                · {minutesText(m.part_minutes)}
              </p>
            )}
          </li>
        ))}
      </ul>
      <p className="text-xs text-gray-500">
        Sales are each rep's own credited sales, shown to that rep, the day's
        closer and lead, and staff. Attendance belongs to the day, not to a rep.
      </p>
    </div>
  );
};

/** The day's lead reopens a closed day (on the day only; reason required). */
export const ReopenDayControl: React.FC<{
  eventId: string;
  day: EventRunDay;
  timezone?: string;
}> = ({ eventId, day, timezone }) => {
  const qc = useQueryClient();
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const mutation = useMutation({
    mutationFn: () => reopenEventDay(day.event_day_id, reason.trim()),
    onSuccess: () => {
      showToast("success", `${dayLabelOf(day.service_date)} reopened`);
      setOpen(false);
      setReason("");
      invalidateAfterDayRecord(qc, eventId);
    },
    onError: (e: Error) => {
      showToast("error", e.message);
      void qc.invalidateQueries({ queryKey: dayRunKeys.state(eventId) });
    },
  });
  if (!day.can_reopen) {
    return null;
  }
  return (
    <div className="space-y-2" data-testid="reopen-day">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full min-h-[44px] rounded-md border border-amber-500 bg-white px-4 text-sm font-semibold text-amber-800"
        >
          Reopen day
        </button>
      ) : (
        <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3">
          <p className="text-xs text-amber-900">
            Reopen if a rep hasn't finished their part. The day's sales are kept
            and counted again when you close; nothing is sent. You can reopen
            until {timeOf(day.reopen_until, timezone)}; close the day again
            before the day ends, or a manager will complete the close
            afterwards.
          </p>
          <input
            type="text"
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why are you reopening? (required)"
            aria-label="Reason for reopening"
            className="block w-full min-h-[40px] rounded-md border border-gray-300 px-2 text-sm"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="flex-1 min-h-[44px] rounded-md border border-gray-300 bg-white text-sm"
            >
              Back
            </button>
            <button
              type="button"
              disabled={mutation.isPending || reason.trim().length < 3}
              onClick={() => mutation.mutate()}
              className="flex-1 min-h-[44px] rounded-md bg-amber-600 text-sm font-semibold text-white disabled:opacity-50"
            >
              {mutation.isPending ? "Reopening…" : "Confirm reopen"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ReopenHistory: React.FC<{ day: EventRunDay; timezone?: string }> = ({
  day,
  timezone,
}) => {
  const r = day.reopens ?? [];
  if (r.length === 0) {
    return null;
  }
  return (
    <p className="text-xs text-amber-800" data-testid="reopen-history">
      Reopened {r.length === 1 ? "once" : `${r.length} times`}; last by{" "}
      {r[0].by_name ?? "the lead"} at {timeOf(r[0].reopened_at, timezone)}: "
      {r[0].reason}".
    </p>
  );
};

// ------------------------------------------------------------------ MSR panel
const MSR_BLOCK_TEXT: Record<string, string> = {
  other_event_open:
    "You're still running another event today: close the day you started there (you close it), or close your part there. Then start this one.",
  not_tagged:
    "You're on this event, but not on the Sales Reps list for this day. Ask your manager to add you for this day.",
  upcoming: "You can start this day on the day itself.",
  ended: "This day has ended.",
  not_tracked: "This day is before day start and close began.",
  lead_only:
    "Only the rep who closes this day closes it. When you're done, close your own part; the closer closes the day for everyone.",
};

// Why a day can't be started/closed, worded for WHO is looking (MSR QA 10-20: an admin opening the MSR page was told
// "You're not on the Sales Reps list for this day" -- true, but it read as if the event's rep was missing).
export function msrBlockText(
  block: string | null | undefined,
  day: {
    reps?: { name: string | null; role: string }[];
    closer_names?: string[];
    closers?: { name: string | null; closer_role: string }[];
  },
  viewer: { is_full_time?: boolean; is_rep?: boolean } | undefined,
  fallback: string,
): string {
  if (block === "lead_only") {
    const list = day.closers ?? [];
    const primary = list
      .filter((c) => c.closer_role === "primary")
      .map((c) => c.name)
      .filter(Boolean);
    const backup = list
      .filter((c) => c.closer_role === "backup")
      .map((c) => c.name)
      .filter(Boolean);
    const names = primary.length
      ? primary
      : (day.closer_names ?? []).filter(Boolean);
    if (!names.length) {
      return MSR_BLOCK_TEXT.lead_only;
    }
    return `${names.join(" or ")} closes today${backup.length ? ` (backup: ${backup.join(", ")})` : ""}. When you're done, close your own part; the closer closes the day for everyone.`;
  }
  if (block === "not_tagged" && viewer?.is_full_time && !viewer?.is_rep) {
    const names = (day.reps ?? []).map((r) => r.name).filter(Boolean);
    return names.length
      ? `You're viewing this as staff. Only the rep on this day's Sales Reps list (${names.join(", ")}) can start or close it.`
      : "You're viewing this as staff. Only a rep on this day's Sales Reps list can start or close it, and no rep is on the list for this day yet.";
  }
  return MSR_BLOCK_TEXT[block ?? ""] ?? fallback;
}

export const MsrEventDayRunPanel: React.FC<{ eventId: string }> = ({
  eventId,
}) => {
  const { data: state, isLoading, error } = useEventDayRunState(eventId);
  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading days…</div>;
  }
  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
        {(error as Error).message}
      </div>
    );
  }
  if (!state?.model_active) {
    return null;
  }
  const days = state.days ?? [];
  if (days.length === 0) {
    return (
      <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
        This event has no dated days yet.
      </div>
    );
  }
  return (
    <section
      className="space-y-4"
      aria-label="Event days"
      data-testid="msr-day-run"
    >
      {days.map((day, i) => (
        <article
          key={day.event_day_id}
          className="space-y-3 rounded-lg border border-gray-200 bg-white p-4"
          data-testid="msr-day-card"
        >
          <header className="flex items-center justify-between gap-2">
            <h2 className="text-base font-semibold text-gray-900">
              {dayLabelOf(day.service_date)}
              {days.length > 1 && (
                <span className="ml-1 text-xs font-normal text-gray-500">
                  Day {i + 1} of {days.length}
                </span>
              )}
            </h2>
            <DayStatusChip day={day} />
          </header>

          {day.status === "closed" && (
            <>
              <DayCloseSummary day={day} state={state} />
              <ReopenHistory day={day} timezone={state.timezone} />
              <DayTeamList day={day} timezone={state.timezone} />
              <ReopenDayControl
                eventId={eventId}
                day={day}
                timezone={state.timezone}
              />
            </>
          )}

          {day.status === "not_started" &&
            (day.can_start ? (
              <DayStartForm
                eventId={eventId}
                day={day}
                eventType={state.event_type}
              />
            ) : (
              <p className="text-sm text-gray-600">
                {msrBlockText(
                  day.start_block,
                  day,
                  state.viewer,
                  "This day can't be started now.",
                )}
              </p>
            ))}

          {day.status === "started" && (
            <>
              <p className="text-sm text-gray-700">
                Started by{" "}
                {day.start?.by_me
                  ? "you"
                  : (day.start?.started_by_name ?? "a rep")}{" "}
                · {timeOf(day.start?.started_at, state.timezone)}
              </p>
              <ReopenHistory day={day} timezone={state.timezone} />
              <MyPartCard
                eventId={eventId}
                day={day}
                timezone={state.timezone}
              />
              <DayTeamList day={day} timezone={state.timezone} />
              {day.can_close ? (
                <DayCloseForm
                  eventId={eventId}
                  day={day}
                  state={state}
                  asManager={false}
                />
              ) : (
                <p className="text-sm text-gray-600">
                  {msrBlockText(
                    day.close_block,
                    day,
                    state.viewer,
                    "You can't close this day.",
                  )}
                </p>
              )}
            </>
          )}

          {day.status === "late" &&
            (day.can_close && day.close_as === "rep" ? (
              // mig 1330 (audit P2-4): the closer's in-grace close, a few minutes after the day's end
              <DayCloseForm
                eventId={eventId}
                day={day}
                state={state}
                asManager={false}
              />
            ) : (
              <p className="rounded-md bg-red-50 p-3 text-sm text-red-800">
                This day wasn't closed on the day. Your manager will complete
                the close.
              </p>
            ))}
        </article>
      ))}
    </section>
  );
};

// ------------------------------------------------------------------ admin / manager panel
const CorrectionForm: React.FC<{
  eventId: string;
  day: EventRunDay;
  state: EventRunState;
}> = ({ eventId, day, state }) => {
  const qc = useQueryClient();
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [att, setAtt] = useState("");
  const [ver, setVer] = useState("");
  const [reason, setReason] = useState("");
  const [add, setAdd] = useState<string[]>([]);
  const [remove, setRemove] = useState<string[]>([]);
  const toggle = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
  const saleChanges = add.length + remove.length > 0;
  const currentSales = (day.close?.sales ?? []).filter((x) => !!x.contact_id);
  const addable = day.close?.addable_signups ?? [];
  const mutation = useMutation({
    mutationFn: () =>
      correctEventDayClose(day.event_day_id, {
        attendance: att.trim() === "" ? null : Number(att),
        // with sale changes the server derives the verified count from the rows
        verifiedCount: saleChanges || ver.trim() === "" ? null : Number(ver),
        reason,
        addContactIds: add,
        removeContactIds: remove,
      }),
    onSuccess: () => {
      showToast("success", "Correction saved (the original close is kept)");
      invalidateAfterDayRecord(qc, eventId);
      setOpen(false);
      setAtt("");
      setVer("");
      setReason("");
      setAdd([]);
      setRemove([]);
    },
    onError: (e: Error) => showToast("error", e.message),
  });
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-sm text-primary-600 underline"
      >
        Correct numbers (admin)
      </button>
    );
  }
  return (
    <div
      className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3"
      data-testid="day-correction-form"
    >
      <p className="text-xs text-amber-900">
        A correction is added on top of the close; the original numbers are
        kept. Leave a field blank to keep its current value.
      </p>
      <div className="grid grid-cols-2 gap-2">
        {state.attendance_source !== "none" && (
          <input
            type="number"
            min={0}
            value={att}
            onChange={(e) => setAtt(e.target.value)}
            placeholder={
              state.attendance_source === "manual" ? "Attendees" : "Check-ins"
            }
            aria-label="Corrected attendance"
            className="min-h-[40px] rounded-md border border-gray-300 px-2 text-sm"
          />
        )}
        <input
          type="number"
          min={0}
          value={saleChanges ? "" : ver}
          disabled={saleChanges}
          onChange={(e) => setVer(e.target.value)}
          placeholder={
            saleChanges ? "Set from the sales below" : "Verified sales"
          }
          aria-label="Corrected verified sales"
          className="min-h-[40px] rounded-md border border-gray-300 px-2 text-sm disabled:bg-gray-100"
        />
      </div>
      {(currentSales.length > 0 || addable.length > 0) && (
        <fieldset
          className="space-y-1 text-sm"
          data-testid="day-correction-sales"
        >
          <legend className="text-xs font-medium text-amber-900">
            Sales of this day (tick to remove or add; the verified count
            follows)
          </legend>
          {currentSales.map((x) => (
            <label
              key={`r-${x.contact_id}`}
              className="flex items-center gap-2"
            >
              <input
                type="checkbox"
                checked={remove.includes(x.contact_id as string)}
                onChange={() =>
                  setRemove((l) => toggle(l, x.contact_id as string))
                }
                aria-label={`Remove ${x.name || "sale"}`}
              />
              <span>
                Remove {x.name || "(no name)"}
                {x.added_by_correction ? " (added by a correction)" : ""}
              </span>
            </label>
          ))}
          {addable.map((x) => (
            <label
              key={`a-${x.contact_id}`}
              className="flex items-center gap-2"
            >
              <input
                type="checkbox"
                checked={add.includes(x.contact_id)}
                onChange={() => setAdd((l) => toggle(l, x.contact_id))}
                aria-label={`Add ${x.name || "sign-up"}`}
              />
              <span>Add {x.name || "(no name)"}</span>
            </label>
          ))}
        </fieldset>
      )}
      <input
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason (required)"
        aria-label="Correction reason"
        className="block w-full min-h-[40px] rounded-md border border-gray-300 px-2 text-sm"
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={
            mutation.isPending ||
            reason.trim().length < 5 ||
            (att.trim() === "" && ver.trim() === "" && !saleChanges)
          }
          onClick={() => mutation.mutate()}
          className="rounded-md bg-amber-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          Save correction
        </button>
      </div>
    </div>
  );
};

const ROUTE_TEXT: Record<string, string> = {
  direct_manager: "the rep's manager was notified",
  superadmin_fallback: "no manager on file, superadmins were notified",
  nobody: "no one could be notified",
};

export const EventDaysStatusPanel: React.FC<{ eventId: string }> = ({
  eventId,
}) => {
  const { data: state, error } = useEventDayRunState(eventId);
  const [openDay, setOpenDay] = useState<string | null>(null);
  if (error || !state?.model_active) {
    // not in the model (or this viewer may not see day status): nothing new on the page
    return null;
  }
  const days = state.days ?? [];
  if (days.length === 0) {
    return null;
  }
  return (
    <section
      className="mb-6 space-y-3 rounded-lg border border-gray-200 bg-white p-4"
      aria-label="Event days"
      data-testid="event-days-status"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-900">
          Event days — start and close
        </h3>
        {state.all_days_closed && state.event_status !== "completed" && (
          <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
            All days closed — ready to mark Completed
          </span>
        )}
      </div>
      <ul className="divide-y divide-gray-100">
        {days.map((day) => (
          <li
            key={day.event_day_id}
            className="space-y-2 py-3"
            data-testid="admin-day-row"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-gray-900">
                {dayLabelOf(day.service_date)}
              </span>
              <DayStatusChip day={day} />
              <span className="text-xs text-gray-500">
                {day.reps.length > 0
                  ? day.reps
                      .map(
                        (r) =>
                          `${r.name ?? "?"}${r.role === "lead" ? " (lead)" : ""}`,
                      )
                      .join(", ")
                  : "No rep on this day"}
              </span>
            </div>
            {day.start && (
              <p className="text-xs text-gray-600">
                Started by {day.start.started_by_name ?? "a rep"} ·{" "}
                {timeOf(day.start.started_at, state.timezone)}
              </p>
            )}
            {day.late_flag && (
              <p className="text-xs text-red-700">
                Flagged late {timeOf(day.late_flag.flagged_at, state.timezone)}{" "}
                —{" "}
                {ROUTE_TEXT[day.late_flag.notify_route] ??
                  day.late_flag.notify_route}
                {day.late_flag.resolved_at ? " · since closed" : ""}
              </p>
            )}
            {day.status === "closed" && (
              <DayCloseSummary day={day} state={state} />
            )}
            <ReopenHistory day={day} timezone={state.timezone} />
            {day.status !== "not_tracked" && (
              <DayTeamList day={day} timezone={state.timezone} />
            )}
            {day.status === "closed" && day.can_correct && (
              <CorrectionForm eventId={eventId} day={day} state={state} />
            )}
            {day.can_close &&
              day.close_as === "manager" &&
              (openDay === day.event_day_id ? (
                <DayCloseForm
                  eventId={eventId}
                  day={day}
                  state={state}
                  asManager
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setOpenDay(day.event_day_id)}
                  className="rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white"
                >
                  Complete the close
                </button>
              ))}
          </li>
        ))}
      </ul>
      {(state.signups_outside_days ?? 0) > 0 && (
        <p className="text-xs text-gray-500">
          {state.signups_outside_days} sign-up(s) are dated outside the event's
          days and are not counted toward any day.
        </p>
      )}
    </section>
  );
};
